<script setup lang="ts">
/**
 * A section that is one line until someone wants it.
 *
 * The admin surface kept growing by addition: every new thing was a new
 * always-open block, so the page that answers "does anything need me?" and
 * the page that answers "show me everything" were the same page. The rule
 * this component exists to make cheap: **what needs a decision is open; what
 * is reference folds away behind a line that says what is in it.**
 *
 * The summary is the point. A fold that only says "Advanced ▸" makes people
 * open it to find out whether they needed to; one that says
 * "reads: igel · model: default" usually saves the click.
 *
 * `tone` lets a folded section still raise its hand — a gap inside a fold
 * must not be quieter than a gap outside one. Callers decide `open` from the
 * data (open when something inside needs attention), so a fold is never
 * where a problem goes to hide.
 */
import { ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    title: string;
    /** What is inside, in a few words. Shown when closed AND open. */
    summary?: string;
    /** Initial state. Re-applied whenever it changes (a new record). */
    open?: boolean;
    tone?: "neutral" | "warn" | "bad";
    /** Heading level of the title, for the document outline. */
    level?: 3 | 4;
  }>(),
  { summary: "", open: false, tone: "neutral", level: 4 },
);

const isOpen = ref(props.open);
watch(
  () => props.open,
  (v) => {
    isOpen.value = v;
  },
);
</script>

<template>
  <section class="fold" :class="`fold--${tone}`">
    <component :is="`h${level}`" class="fold__heading">
      <button
        type="button"
        class="fold__bar"
        :aria-expanded="isOpen"
        @click="isOpen = !isOpen"
      >
        <span class="fold__title">{{ title }}</span>
        <span class="fold__summary">{{ summary }}</span>
        <span class="fold__chev" aria-hidden="true">{{
          isOpen ? "▾" : "▸"
        }}</span>
      </button>
    </component>
    <div v-if="isOpen" class="fold__body">
      <slot />
    </div>
  </section>
</template>

<style scoped>
.fold {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.fold--warn {
  border-left: 3px solid var(--color-warn);
}
.fold--bad {
  border-left: 3px solid var(--color-error);
}
.fold__heading {
  margin: 0;
  font: inherit;
}
.fold__bar {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  width: 100%;
  padding: 0.5rem 0.7rem;
  font: inherit;
  color: inherit;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
}
.fold__title {
  font-weight: 600;
  white-space: nowrap;
}
.fold__summary {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  color: var(--color-muted);
}
.fold--warn .fold__summary {
  color: var(--color-warn-ink);
}
.fold--bad .fold__summary {
  color: var(--color-error);
}
.fold__chev {
  color: var(--color-muted);
}
.fold__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: 0 0.7rem 0.7rem;
}
</style>
